/**
 * Plan d'entraînement — règles PURES, sans base de données.
 *
 * Tout ce qui se calcule à partir de dates et de nombres vit ici pour être
 * testé sans serveur (`tests/plan.test.ts`) et partagé entre le serveur
 * (`plan.service.ts`) et l'écran (`PlanView`). Spec : spec/plan-entrainement.md.
 */

export type PlanItemType = "campaign" | "challenge" | "path" | "evaluation" | "session";

/** État affiché d'une campagne dans le plan (dérivé, jamais stocké tel quel). */
export type CampaignPlanState = "draft" | "planned" | "active" | "closed";

/**
 * Couleur par TYPE d'élément (décision D3). Une seule source : l'écran, la
 * légende et le PDF lisent ce registre. Les hex sont ceux de la charte
 * (`styles/globals.css`, `domain-theme.ts`).
 */
export const PLAN_TYPE_THEME: Record<PlanItemType, { color: string; fill: string; border: string }> = {
  campaign:   { color: "#384872", fill: "#E7ECF7", border: "#384872" },
  challenge:  { color: "#926B00", fill: "#FBF1D6", border: "#DEB94A" },
  path:       { color: "#5A4BBE", fill: "#EEEAF9", border: "#988EF2" },
  evaluation: { color: "#2A5C8A", fill: "#E3F1FD", border: "#A6D3F9" },
  session:    { color: "#1E7A3C", fill: "#E6F3EB", border: "#7CBF94" },
};

/** Trait « aujourd'hui » et séance manquée : le corail de la charte. */
export const PLAN_TODAY_COLOR = "#C0423F";

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Fenêtre d'appariement d'une séance planifiée : le jour prévu ± 1 jour (§2.4). */
export const PLAN_MATCH_WINDOW_DAYS = 1;

/** Espace laissé entre deux campagnes proposées par « Construis mon plan ». */
export const PLAN_GAP_DAYS = 14;

/** Fenêtre par défaut d'un parcours affecté sans échéance (§2.4). */
export const PATH_DEFAULT_WINDOW_DAYS = 42;

/** Durée par défaut d'une campagne créée d'un tracé sans durée. */
export const CAMPAIGN_DEFAULT_DAYS = 42;

/**
 * Dates RÉELLES d'une campagne qu'on lance maintenant (règle unique, lue par le lancement ET par
 * l'écran de confirmation) :
 *  - sans date (« à définir ») : début aujourd'hui, fin = durée prévue (option A du 28/09/2026) ;
 *  - début futur : le début est AVANCÉ à aujourd'hui, À MINUIT (une session jouée le matin même
 *    compte : audit du 29/09/2026), la fin reste INCHANGÉE (décision du 29/09/2026 : aucune règle
 *    ne la décalait) ;
 *  - début passé ou aujourd'hui : dates inchangées.
 */
export function launchDates(now: Date, startsAt: Date | null, endsAt: Date | null, plannedDays: number | null | undefined): { start: Date; end: Date | null; advanced: boolean } {
  if (!startsAt) { const d = datesFromStart(now, plannedDays); return { start: d.start, end: endsAt ?? d.end, advanced: false }; }
  if (startsAt.getTime() > now.getTime()) return { start: startOfDay(now), end: endsAt, advanced: true };
  return { start: startsAt, end: endsAt, advanced: false };
}

/**
 * Dates d'une campagne dont le début est fixé : fin = début + durée prévue (sinon 6 semaines).
 * Sert au choix des dates dans la fiche ET au lancement sans date (option A : début aujourd'hui).
 */
export function datesFromStart(start: Date, plannedDays: number | null | undefined): { start: Date; end: Date } {
  const days = plannedDays && plannedDays > 0 ? plannedDays : CAMPAIGN_DEFAULT_DAYS;
  const s = startOfDay(start);
  return { start: s, end: new Date(s.getTime() + days * 86_400_000) };
}

// ── Dates ────────────────────────────────────────────────────────────────────

/**
 * Un jour saisi est-il une VRAIE date `AAAA-MM-JJ` (format ET calendrier : pas de 31 février) ?
 * Sans ce contrôle, une valeur invalide partait en base et répondait 500 (audit du lot 4, 29/09/2026).
 */
export function isRealIsoDay(v: unknown): v is string {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d;
}

/** `YYYY-MM-DD` en calendrier local (le plan raisonne en jours civils). */
export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Date locale à minuit depuis `YYYY-MM-DD` (jamais `new Date(iso)`, qui lit en UTC). */
export function fromISODate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Lundi de la semaine (ISO) contenant `d`, à minuit local. */
export function weekStart(d: Date): Date {
  const s = startOfDay(d);
  const dow = (s.getDay() + 6) % 7; // 0 = lundi
  return addDays(s, -dow);
}

export function sameWeek(a: Date, b: Date): boolean {
  return weekStart(a).getTime() === weekStart(b).getTime();
}

/** Écart en jours civils entre deux dates (b − a), signé. */
export function diffDays(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
}

// ── Séances planifiées ───────────────────────────────────────────────────────

/**
 * Jours de la semaine retenus selon le rythme, pour étaler les séances au lieu
 * de les empiler le lundi. 0 = lundi … 4 = vendredi.
 */
const WEEKDAY_PATTERNS: Record<number, number[]> = {
  1: [2],
  2: [1, 3],
  3: [0, 2, 4],
  4: [0, 1, 3, 4],
  5: [0, 1, 2, 3, 4],
};

/**
 * Répartit `perWeek` séances par semaine entre `from` et `to` (bornes incluses),
 * jours ouvrés seulement. Retourne des `YYYY-MM-DD`, dans l'ordre.
 *
 * Une semaine entamée (campagne qui démarre un jeudi) ne reçoit que les jours
 * du motif encore à venir : on ne rattrape pas ce qui est déjà passé.
 */
export function distributeSessionDays(from: Date, to: Date, perWeek: number): string[] {
  return distributeSessionDaysOn(from, to, defaultWeekdays(Math.max(1, Math.round(perWeek))));
}

// ── Jours par participant (décision 1.1, 25/09/2026) ─────────────────────────────────────────
// Un jour = numéro ISO du jour ouvré : 1 = lundi … 5 = vendredi. Jamais le week-end.

/**
 * Une saisie de jours est-elle ACCEPTABLE ? `null` (la ligne suit la campagne) ou un tableau
 * d'entiers de 1 (lundi) à 5 (vendredi) ; un tableau vide = aucune session, permis. Tout le reste
 * est refusé — `cleanWeekdays` le transformait en « 0 session » sans rien dire (avenant du lot 3).
 */
export function isValidWeekdaysInput(raw: unknown): boolean {
  if (raw === null) return true;
  return Array.isArray(raw) && raw.every((d) => typeof d === "number" && Number.isInteger(d) && d >= 1 && d <= 5);
}
export const INVALID_WEEKDAYS_MESSAGE = "Jours invalides : choisissez des jours du lundi (1) au vendredi (5).";

/** Jours ouvrés valides, sans doublon, triés. */
export function cleanWeekdays(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((d) => Number(d)).filter((d) => Number.isInteger(d) && d >= 1 && d <= 5))].sort((a, b) => a - b);
}

/** Jours par défaut d'un rythme : 1 → mer ; 2 → mar, jeu ; 3 → lun, mer, ven ; 4 → lun, mar, jeu, ven ; 5 → lun → ven. */
export function defaultWeekdays(perWeek: number): number[] {
  const n = Math.max(0, Math.min(5, Math.round(perWeek)));
  return n === 0 ? [] : WEEKDAY_PATTERNS[n]!.map((offset) => offset + 1);
}

/**
 * Les jours retenus pour UN participant. Priorité : ses jours à lui (grille personnalisée),
 * puis son ancien rythme individuel (nombre seul, avant la grille), puis les jours de la
 * campagne, puis le motif par défaut du rythme de la campagne. Le rythme d'un participant =
 * le nombre de ses jours (0 = aucune session).
 */
export function resolveMemberWeekdays(input: {
  memberWeekdays?: number[] | null;
  memberPerWeek?: number | null;
  campaignWeekdays?: number[] | null;
  campaignPerWeek?: number | null;
}): number[] {
  if (input.memberWeekdays) return cleanWeekdays(input.memberWeekdays);
  if (input.memberPerWeek != null) return defaultWeekdays(input.memberPerWeek);
  if (input.campaignWeekdays && input.campaignWeekdays.length > 0) return cleanWeekdays(input.campaignWeekdays);
  return defaultWeekdays(input.campaignPerWeek ?? 0);
}

// ── Grille de rythme (décisions 1.1 et 1.11, 25/09/2026) ─────────────────────────────────────
// Une ligne par participant, une case par jour ouvré. Règles pures, partagées par l'écran
// (`RhythmGrid`) et le serveur (`readRhythmGrid`, `rhythmOverlaps`).

/** Une ligne de la grille : ses jours, et si elle a été personnalisée à la main. */
export interface RhythmRow { days: number[]; personalized: boolean }

/**
 * La ligne de RÉFÉRENCE de la grille : les jours de la ligne « Tous les participants » (décision du
 * 29/09/2026). Un nombre = le motif par défaut de ce rythme (2 → mardi, jeudi) ; un tableau = les jours
 * choisis sur la ligne « Tous ». Les lignes non personnalisées la suivent.
 */
export type RhythmBase = number | number[];
function baseDaysOf(base: RhythmBase): number[] {
  return Array.isArray(base) ? cleanWeekdays(base) : defaultWeekdays(base);
}

/** Lignes de départ : les jours enregistrés d'un participant, sinon la ligne de référence. */
export function initialRhythmRows(memberIds: string[], base: RhythmBase, saved: Record<string, number[] | null | undefined> = {}): Record<string, RhythmRow> {
  const def = baseDaysOf(base);
  return Object.fromEntries(memberIds.map((id) => {
    const days = saved[id] ? cleanWeekdays(saved[id]) : def;
    return [id, { days, personalized: days.join(",") !== def.join(",") }];
  }));
}

/** Nouvelle référence (rythme ou ligne « Tous ») : seules les lignes NON personnalisées la prennent (décision 1.1). */
export function recolorRhythmRows(rows: Record<string, RhythmRow>, base: RhythmBase): Record<string, RhythmRow> {
  const def = baseDaysOf(base);
  return Object.fromEntries(Object.entries(rows).map(([id, r]) => [id, r.personalized && r.days.join(",") !== def.join(",") ? r : { days: def, personalized: false }]));
}

/** Clic sur une case : jour retiré s'il était retenu, ajouté sinon. La ligne devient personnalisée si elle s'écarte de la référence. */
export function toggleRhythmDay(rows: Record<string, RhythmRow>, memberId: string, day: number, base: RhythmBase): Record<string, RhythmRow> {
  const cur = rows[memberId]?.days ?? [];
  const days = cleanWeekdays(cur.includes(day) ? cur.filter((d) => d !== day) : [...cur, day]);
  return { ...rows, [memberId]: { days, personalized: days.join(",") !== baseDaysOf(base).join(",") } };
}

/** « Suivre la ligne Tous » : la ligne reprend les jours de référence et redevient non personnalisée. */
export function followBaseRow(rows: Record<string, RhythmRow>, memberId: string, base: RhythmBase): Record<string, RhythmRow> {
  return { ...rows, [memberId]: { days: baseDaysOf(base), personalized: false } };
}

/**
 * Clic sur une case de la ligne « Tous » : ajoute ou retire ce jour. Refusé (`null`) s'il ne resterait
 * aucun jour — pour ne poser aucune session, on choisit « Aucune session » dans le rythme.
 */
export function toggleBaseDay(base: number[], day: number): number[] | null {
  const next = cleanWeekdays(base.includes(day) ? base.filter((d) => d !== day) : [...base, day]);
  return next.length === 0 ? null : next;
}

/**
 * Jours déjà pris par d'AUTRES campagnes, par participant et par jour (décision 1.11 : cumuler
 * est permis, la grille le signale sans griser). `others` : chaque campagne avec les jours de
 * chacun de ses participants.
 */
export function weekdayOverlaps(memberIds: string[], others: { id: string; title: string; daysByMember: Record<string, number[]> }[]): Record<string, Record<number, { id: string; title: string }[]>> {
  const out: Record<string, Record<number, { id: string; title: string }[]>> = {};
  for (const m of memberIds) {
    for (const o of others) {
      for (const d of cleanWeekdays(o.daysByMember[m] ?? [])) {
        ((out[m] ??= {})[d] ??= []).push({ id: o.id, title: o.title });
      }
    }
  }
  return out;
}

/**
 * Le geste proposé pour un participant SANS licence au choix des participants (décisions 1.5 et
 * 1.5 bis) : attribuer depuis le pool si le lecteur en a le droit (calculé par le serveur) ET qu'il
 * reste des places ; sinon, pool vide et droit d'acheter (ORG / ADM) → acheter (nouvel onglet) ;
 * sinon → demander aux organisateurs. Jamais d'action impossible. `null` : il a la licence.
 */
export function licenceActionFor(member: { status: "ok" | "no_licence" | "disabled"; canAssign: boolean }, poolAvailable: number, canBuy: boolean): "assign" | "buy" | "ask" | null {
  if (member.status !== "no_licence") return null;
  if (member.canAssign && poolAvailable > 0) return "assign";
  if (poolAvailable <= 0 && canBuy) return "buy";
  return "ask";
}

/** Les jours de sessions entre `from` et `to` (bornes incluses), sur les jours ouvrés donnés. */
export function distributeSessionDaysOn(from: Date, to: Date, weekdays: number[]): string[] {
  const pattern = cleanWeekdays(weekdays).map((d) => d - 1);
  if (pattern.length === 0) return [];
  const start = startOfDay(from);
  const end = startOfDay(to);
  if (end < start) return [];
  const out: string[] = [];
  let monday = weekStart(start);
  while (monday <= end) {
    for (const offset of pattern) {
      const day = addDays(monday, offset);
      if (day >= start && day <= end) out.push(toISODate(day));
    }
    monday = addDays(monday, 7);
  }
  return out;
}

/**
 * Remet les sessions À VENIR d'un participant en accord avec ses jours (décision 1.1) :
 * rythme à la hausse comme à la baisse, sans jamais toucher au passé. Une session déplacée
 * à la main (`movedFrom`) est conservée, et son jour d'origine n'est pas reposé. Une session
 * dont le collaborateur a demandé le déplacement (`moveRequestedTo`, demande pas encore traitée)
 * est conservée elle aussi, avec sa demande (décision de Valérie, 28/09/2026 : une demande ne
 * disparaît jamais en silence).
 * Renvoie les sessions à retirer et les jours à ajouter.
 */
export function reconcilePlannedDays(input: {
  desired: string[];
  existing: { id: string; day: string | null; status: string; movedFrom?: string | null; moveRequestedTo?: string | null }[];
  today: string;
}): { toDelete: string[]; toInsert: string[] } {
  const futures = input.existing.filter((e) => e.status === "planned" && e.day != null && e.day >= input.today);
  const voulus = new Set(input.desired.filter((d) => d >= input.today));
  const deplacesDepuis = new Set(futures.filter((e) => e.movedFrom).map((e) => e.movedFrom!));
  const toDelete = futures.filter((e) => !e.movedFrom && !e.moveRequestedTo && !voulus.has(e.day!)).map((e) => e.id);
  // Un jour déjà occupé (quel que soit le statut) ou dont la session a été déplacée n'est pas reposé.
  const occupes = new Set(input.existing.filter((e) => e.day != null && !toDelete.includes(e.id)).map((e) => e.day!));
  const toInsert = [...voulus].filter((d) => !occupes.has(d) && !deplacesDepuis.has(d)).sort();
  return { toDelete, toInsert };
}

/** Une session jouée à `at` compte pour une séance prévue le jour `day` si |écart| ≤ 1 jour. */
export function inMatchWindow(day: string, at: Date): boolean {
  return Math.abs(diffDays(fromISODate(day), at)) <= PLAN_MATCH_WINDOW_DAYS;
}

/**
 * Parmi des séances planifiées candidates, celle qu'une session jouée à `at`
 * doit valider : la plus proche du jour joué, et à égalité la plus ancienne
 * (on solde d'abord ce qui est en retard).
 */
export function pickPlannedToMatch<T extends { day: string; createdAt?: Date }>(candidates: T[], at: Date): T | null {
  const eligible = candidates.filter((c) => inMatchWindow(c.day, at));
  if (eligible.length === 0) return null;
  eligible.sort((a, b) => {
    const da = Math.abs(diffDays(fromISODate(a.day), at));
    const db = Math.abs(diffDays(fromISODate(b.day), at));
    if (da !== db) return da - db;
    return a.day < b.day ? -1 : a.day > b.day ? 1 : 0;
  });
  return eligible[0]!;
}

/** Une séance encore « planned » est manquée quand son jour + 1 est révolu. */
export function isMissed(day: string, now: Date): boolean {
  return diffDays(fromISODate(day), now) > PLAN_MATCH_WINDOW_DAYS;
}

/**
 * Déplacement par le collaborateur (D7) : libre dans la même semaine, sinon
 * c'est une demande adressée au manager.
 */
export function classifyMove(fromDay: string, toDay: string): "move" | "request" {
  return sameWeek(fromISODate(fromDay), fromISODate(toDay)) ? "move" : "request";
}

/** Une personne telle que la règle de déplacement la voit. */
export interface MovePerson { id: string; profileType: string | null; status: string | null; firstName?: string | null; lastName?: string | null; email?: string | null }

/**
 * Déplacer SA session (écran « Mes sessions ») — décisions 3.5 et 3.5 bis de Valérie (25/09/2026).
 * Règle unique, pure, testée par exécution (tableau complet dans `tests/seances.test.ts`).
 *
 * 1. Dans la semaine en cours (jour d'origine ET nouveau jour) : libre.
 * 2. Au-delà :
 *    - ORG : libre ;
 *    - MNG créateur de la campagne : libre ;
 *    - MNG non créateur : demande au CRÉATEUR de la campagne ;
 *    - BNF (décisions Q5 et R1 de Valérie, 30/09/2026) : demande à son MANAGER DIRECT s'il est MNG
 *      ou ORG et ACTIF (un manager créé ou invité qui n'a jamais activé son compte ne la reçoit
 *      pas) ; sinon (pas de manager, manager non actif, manager d'un autre profil) au RESPONSABLE
 *      DU COMPTE : l'ORG actif, à défaut le GES actif (`accountOrg`, résolu par l'appelant) — plus
 *      de repli sur le créateur de la campagne ;
 *    - tout autre profil : demande à son manager direct, sinon au créateur (inchangé).
 * 3. Si le destinataire est désactivé, ou est un ADM / COO de Kestio : demande à l'ORG du compte.
 * 4. S'il n'existe aucun destinataire : déplacement libre plutôt qu'une demande perdue.
 */
export function decideSessionMove(input: {
  fromDay: string; toDay: string; now: Date;
  actor: { id: string; profileType: string | null };
  /** Créateur de la campagne de la session (null hors campagne). */
  creator: MovePerson | null;
  /** Manager direct de la personne. */
  directManager: MovePerson | null;
  /** Responsable du compte (le repli) : l'ORG actif, à défaut le GES actif (décision R1 du 30/09/2026). */
  accountOrg: MovePerson | null;
}): { kind: "move" } | { kind: "request"; to: MovePerson } {
  const today = input.now;
  if (sameWeek(fromISODate(input.fromDay), today) && sameWeek(fromISODate(input.toDay), today)) return { kind: "move" };
  const p = input.actor.profileType;
  if (p === "ORG") return { kind: "move" };
  if (p === "MNG" && input.creator?.id === input.actor.id) return { kind: "move" };
  const unreachable = (x: MovePerson | null) => !x || x.status === "disabled" || x.profileType === "ADM" || x.profileType === "COO";
  if (p === "BNF") {
    const m = input.directManager;
    const to = m && !unreachable(m) && m.status === "active" && (m.profileType === "MNG" || m.profileType === "ORG") ? m : input.accountOrg;
    if (!to || unreachable(to) || to.id === input.actor.id) return { kind: "move" };
    return { kind: "request", to };
  }
  let to: MovePerson | null = p === "MNG" ? input.creator : (input.directManager ?? input.creator);
  if (to && unreachable(to)) to = input.accountOrg;
  if (!to || unreachable(to) || to.id === input.actor.id) return { kind: "move" };
  return { kind: "request", to };
}

// ── Campagnes ────────────────────────────────────────────────────────────────

export interface CampaignObjective {
  criterionKey: string;
  /**
   * Textes du critère, calculés À L'AFFICHAGE dans la langue du lecteur (décision 5.2, lot 8) —
   * jamais enregistrés : `campaignObjectiveOf` ignore un ancien `criterionLabel` figé en base (retiré
   * par la migration 20260930-1631), sans quoi chaque réécriture de l'objet (photo, clôture) le recopiait.
   */
  criterionLabel?: string;
  criterionBehavior?: string | null;
  criterionHelp?: string | null;
  levelStart: number | null;
  levelTarget: number | null;
  snapshotBefore?: { at: string; level: number | null; byUser: Record<string, number>; window?: number } | null;
  snapshotAfter?: { at: string; level: number | null; byUser: Record<string, number>; window?: number } | null;
  verdict?: CampaignVerdict | null;
}

/**
 * `successCriteria` est TOUJOURS un objet (lot 3, point 7) : l'objectif chiffré quand la campagne
 * en porte un, `{}` sinon. L'ancien format tableau (`[{ key, kind, label }]`, création d'une
 * campagne par situation, jamais lu comme objectif) devient `{}` : `normalizeSuccessCriteria`
 * et la migration 20260928 de rattrapage.
 */
export function normalizeSuccessCriteria(raw: unknown): Record<string, unknown> {
  // Un tableau n'a jamais été lu comme un objectif (`campaignObjectiveOf` le refusait) : le
  // convertir en objectif changerait la campagne (courbe, verdict). Il devient `{}`.
  if (Array.isArray(raw)) return {};
  if (raw && typeof raw === "object") {
    // Jamais de texte de critère en base (décision 5.2, lot 8) : un écran qui renverrait l'objectif
    // tel qu'il l'a lu (libellé, comportement, aide dans SA langue) ne le fige pas.
    const { criterionLabel: _l, criterionBehavior: _b, criterionHelp: _h, ...propre } = raw as Record<string, unknown>;
    return propre;
  }
  return {};
}

export function campaignObjectiveOf(raw: unknown): CampaignObjective | null {
  if (!raw || Array.isArray(raw) || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.criterionKey !== "string" || !o.criterionKey) return null;
  return {
    criterionKey: o.criterionKey,
    levelStart: typeof o.levelStart === "number" ? o.levelStart : null,
    levelTarget: typeof o.levelTarget === "number" ? o.levelTarget : null,
    snapshotBefore: (o.snapshotBefore as CampaignObjective["snapshotBefore"]) ?? null,
    snapshotAfter: (o.snapshotAfter as CampaignObjective["snapshotAfter"]) ?? null,
    verdict: (o.verdict as CampaignObjective["verdict"]) ?? null,
  };
}

/**
 * État d'une campagne tel que le plan l'affiche.
 * `planned` = brouillon dont le dispositif est complet : elle partira seule.
 */
export function deriveCampaignState(c: { status: string; ready: boolean }): CampaignPlanState {
  if (c.status === "closed") return "closed";
  if (c.status === "active") return "active";
  return c.ready ? "planned" : "draft";
}

/** État d'un CHALLENGE tel que le plan l'affiche (grille, panneau, légende). */
export type ChallengePlanState = "planned" | "active" | "paused" | "closed";

/**
 * État affiché d'un challenge dans le plan. Un challenge EN PAUSE garde son propre état
 * (« En pause ») : il était rangé avec les brouillons (« Planifié »), alors que ses inscrits
 * gardent leur place et que ses parties d'avant la pause comptent (décisions du 30/09/2026).
 */
export function challengePlanState(status: string | null | undefined): ChallengePlanState {
  if (status === "ended") return "closed";
  if (status === "active") return "active";
  if (status === "paused") return "paused";
  return "planned";
}

/** Ce que l'état autorise (matrice §2.4). */
/**
 * Dates d'une campagne — RÈGLE UNIQUE (décision 1.6 du 25/09/2026), appliquée partout : fiche,
 * Plan, cockpit, étirement d'un enfant. Brouillon : tout se modifie. Lancée : on peut la
 * PROLONGER, jamais la raccourcir, et son début ne bouge plus. Close : rien ne bouge.
 * Renvoie la raison du refus, ou `null` si le changement est permis. Comparaison au jour près.
 */
export function campaignDatesRefusal(
  status: string,
  current: { start: Date | null; end: Date | null },
  next: { start: Date; end: Date },
): null | "closed" | "startLocked" | "shorten" {
  if (status === "closed") return "closed";
  if (status !== "active") return null;
  const jour = (d: Date) => toISODate(startOfDay(d));
  if (current.start && jour(next.start) !== jour(current.start)) return "startLocked";
  if (current.end && jour(next.end) < jour(current.end)) return "shorten";
  return null;
}

/**
 * Objectif d'une campagne — RÈGLE UNIQUE (décision 1.9 du 25/09/2026), appliquée par le serveur
 * et lue par l'écran. Brouillon : libre. Lancée : la compétence est FIGÉE (une autre compétence
 * = une nouvelle campagne), le niveau visé ne peut que MONTER (la mesure « avant » est gardée).
 * Close : rien ne change. Renvoie la raison du refus, ou `null`.
 */
export function objectiveChangeRefusal(
  status: string,
  prev: { criterionKey: string; levelTarget: number | null } | null,
  next: { criterionKey: string; levelTarget: number | null },
): null | "closed" | "criterionLocked" | "levelDown" {
  if (status === "closed") return "closed";
  if (status !== "active" || !prev) return null;
  if (next.criterionKey !== prev.criterionKey) return "criterionLocked";
  if (prev.levelTarget != null && (next.levelTarget == null || next.levelTarget < prev.levelTarget)) return "levelDown";
  return null;
}

export const OBJECTIVE_REFUSAL_MESSAGE: Record<"closed" | "criterionLocked" | "levelDown", string> = {
  closed: "Une campagne terminée ne se modifie plus.",
  criterionLocked: "La compétence d'une campagne lancée ne change plus : pour en travailler une autre, créez une nouvelle campagne.",
  levelDown: "Le niveau visé d'une campagne lancée ne peut que monter.",
};

/** Message (FR) d'un refus de dates — les codes seront traduits au lot 9. */
export const CAMPAIGN_DATES_REFUSAL_MESSAGE: Record<"closed" | "startLocked" | "shorten", string> = {
  closed: "Une campagne terminée ne se modifie plus.",
  startLocked: "Une campagne en cours ne se décale plus : seule sa fin peut bouger.",
  shorten: "Une campagne lancée peut être prolongée, jamais raccourcie.",
};

export function campaignPermissions(state: CampaignPlanState) {
  return {
    move: state === "draft" || state === "planned",
    resizeEnd: state !== "closed",
    editScope: state === "draft" || state === "planned",
    addChild: state !== "closed",
    delete: state === "draft" || state === "planned",
    launchNow: state === "planned",
    closeNow: canCloseCampaignStatus(state),
  };
}

/**
 * Clôturer = campagne EN COURS seulement (décision 6.5). Règle unique : l'écran (`campaignGestures`)
 * et les DEUX routes serveur de clôture (`/stop`, `/close`). Clore un brouillon enverrait un bilan à
 * des participants qui n'ont rien commencé (audit lot 4).
 */
export function canCloseCampaignStatus(status: string): boolean {
  return status === "active";
}
export const CAMPAIGN_CLOSE_REFUSAL_MESSAGE = "Cette campagne n'est pas lancée : supprimez-la ou lancez-la.";

/**
 * Gestes AFFICHÉS sur une campagne — règle unique de l'écran, alignée sur le serveur
 * (règle « jamais d'action impossible », 25/09/2026). Croise l'état (matrice ci-dessus)
 * et les deux rôles calculés par le serveur (`canPilotCampaign`, `coachMemberIdsOf`) :
 * - pilotage (préparer, lancer, clôturer, dates, objectif, participants, supprimer) :
 *   seulement si le lecteur PILOTE la campagne ;
 * - encadrement (grille / rythme, séances) : pour les participants qu'il ENCADRE.
 * Le serveur refuse exactement ce que cette fonction masque : ne jamais conditionner un
 * geste de campagne autrement qu'avec elle.
 */
/**
 * Participants d'une campagne : modifiables tant qu'elle n'est pas CLÔTURÉE (décision D7 du
 * 30/09/2026) — son bilan est figé. Règle unique, lue par le serveur ET par la fiche campagne.
 */
export function canEditCampaignMembers(status: string): boolean {
  return status !== "closed";
}
export const CAMPAIGN_MEMBERS_CLOSED_MESSAGE = "Cette campagne est clôturée : ses participants ne se modifient plus.";
export const CAMPAIGN_MEMBERS_CLOSED_CODE = "campaign_closed_members";

export function campaignGestures(
  state: CampaignPlanState,
  viewer: { canEdit: boolean; canPilot: boolean; coachMemberIds: readonly string[] },
) {
  const p = campaignPermissions(state);
  const pilot = viewer.canEdit && viewer.canPilot;
  return {
    prepare: pilot && state === "draft",
    launch: pilot && (p.launchNow || state === "draft"),
    close: pilot && p.closeNow,
    move: pilot && p.move,
    extend: pilot && p.resizeEnd,
    addChild: pilot && p.addChild,
    objective: pilot && state !== "closed",
    /** Ajuster les participants (décision D7) : jamais une campagne clôturée. */
    members: pilot && canEditCampaignMembers(state),
    // Serveur : brouillon seulement (`status === "draft"` couvre les états draft et planned).
    delete: pilot && p.delete,
    /** Dupliquer (décision 1.10) : quiconque peut créer une campagne et la voit, y compris close. */
    duplicate: viewer.canEdit,
    /** Participants dont le lecteur peut régler la grille / le rythme et déplacer les séances. */
    coachMemberIds: viewer.canEdit && state !== "closed" ? [...viewer.coachMemberIds] : [],
  };
}

/** Rangement d'une campagne dans la liste Campagnes (filtre par état, décision du 29/09/2026). */
export type CampaignBucket = "running" | "upcoming" | "draft" | "done";

/**
 * Règle reprise de l'ancien écran « Actions d'entraînement » : terminée si close OU si sa période
 * est passée (une campagne « en cours » de mars ne l'est plus en septembre) ; sinon brouillon, à
 * venir (début futur) ou en cours. Dates « à définir » : brouillon. `today` au format AAAA-MM-JJ.
 */
export function campaignBucket(status: string, startsAt: string | null, endsAt: string | null, today: string): CampaignBucket {
  const s = startsAt ? startsAt.slice(0, 10) : null;
  const e = endsAt ? endsAt.slice(0, 10) : null;
  if (status === "closed" || (e && e < today && status !== "draft")) return "done";
  if (status === "draft") return "draft";
  return s && s > today ? "upcoming" : "running";
}

/** Borne un enfant aux dates de sa campagne (il ne peut pas en sortir). */
/**
 * Fin d'un challenge ou d'un parcours quand sa campagne change de dates (décision 1.6, Q3 du 29/09/2026) :
 *  - il finissait AVEC la campagne (même jour que son ancienne fin), ou n'avait pas de fin : il suit la
 *    NOUVELLE fin — prolonger la campagne prolonge son challenge et ses parcours ;
 *  - il finissait plus tôt (raccourci exprès) : il garde sa durée, décalé du même écart que le début.
 * Le résultat est ensuite borné à la campagne (`clampToParent`).
 */
export function childEndAfterCampaignMove(childEnd: Date | null, oldCampaignEnd: Date | null, newCampaignEnd: Date, shiftDays: number): Date {
  if (!childEnd) return newCampaignEnd;
  if (oldCampaignEnd && toISODate(childEnd) >= toISODate(oldCampaignEnd)) return newCampaignEnd;
  return addDays(childEnd, shiftDays);
}

export function clampToParent(
  start: Date, end: Date, parentStart: Date | null, parentEnd: Date | null,
): { start: Date; end: Date } {
  let s = start, e = end;
  if (parentStart && s < parentStart) s = parentStart;
  if (parentEnd && e > parentEnd) e = parentEnd;
  if (e < s) e = s;
  return { start: s, end: e };
}

/**
 * Verdict calculé à la clôture (levier 3). Avec objectif : la progression du
 * niveau ; sans : la participation. Jamais « manqué » quand quelque chose a bougé.
 */
/**
 * Verdict d'une campagne close. `unmeasured` (« Résultat non mesuré ») : BizCoach ne peut pas le
 * calculer (pas de niveau visé, ou pas de niveau d'arrivée mesuré) — état NEUTRE, jamais « Non atteint » par défaut
 * (décision de Valérie, 28/09/2026, avec la suppression du verdict manuel).
 */
export type CampaignVerdict = "achieved" | "partial" | "missed" | "unmeasured";

export function computeVerdict(input: {
  levelStart: number | null;
  levelTarget: number | null;
  levelEnd: number | null;
  participationRate: number; // 0..1
}): CampaignVerdict {
  const { levelStart, levelTarget, levelEnd, participationRate } = input;
  // Sans objectif chiffré ou sans mesure d'arrivée, la participation seule ne dit rien de l'objectif :
  // elle faisait écrire « Atteint » (70 % ont joué) ou « Non atteint » (personne n'a joué).
  if (levelTarget == null || levelEnd == null) return "unmeasured";
  if (levelEnd >= levelTarget) return "achieved";
  if (levelStart != null && levelEnd - levelStart >= 0.2) return "partial";
  return participationRate > 0 ? "partial" : "missed";
}

// ── « Construis mon plan » ───────────────────────────────────────────────────

export interface ProposedSlot {
  intentKey: string;
  startsAt: string; // ISO date
  endsAt: string;   // ISO date
  days: number;
}

/**
 * Enchaîne les campagnes demandées sur la période, dans l'ordre donné, en
 * laissant `PLAN_GAP_DAYS` entre deux et en évitant les fenêtres déjà occupées
 * (`busy`, bornes incluses). Ce qui ne tient plus dans la période est omis :
 * le manager le voit et arbitre.
 *
 * Première campagne : jamais avant `earliest` (aujourd'hui + 7 jours par
 * défaut, le temps d'avertir l'équipe).
 */
export function proposeSlots(input: {
  from: Date;
  to: Date;
  earliest?: Date;
  intents: { key: string; days: number }[];
  busy?: { start: Date; end: Date }[];
}): ProposedSlot[] {
  const busy = (input.busy ?? []).map((b) => ({ start: startOfDay(b.start), end: startOfDay(b.end) }))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  const to = startOfDay(input.to);
  let cursor = startOfDay(input.earliest ?? input.from);
  if (cursor < startOfDay(input.from)) cursor = startOfDay(input.from);
  // On démarre un lundi : une campagne qui commence un mercredi perd sa première semaine.
  cursor = nextMonday(cursor);

  const out: ProposedSlot[] = [];
  for (const intent of input.intents) {
    const days = Math.max(7, intent.days || CAMPAIGN_DEFAULT_DAYS);
    let start = cursor;
    // Sauter les fenêtres occupées qui chevauchent [start, start+days].
    for (let guard = 0; guard < 50; guard++) {
      const end = addDays(start, days - 1);
      const clash = busy.find((b) => !(end < b.start || start > b.end));
      if (!clash) break;
      start = nextMonday(addDays(clash.end, PLAN_GAP_DAYS));
    }
    const end = addDays(start, days - 1);
    if (end > to) break;
    out.push({ intentKey: intent.key, startsAt: toISODate(start), endsAt: toISODate(end), days });
    cursor = nextMonday(addDays(end, PLAN_GAP_DAYS));
  }
  return out;
}

/** Le lundi de `d` si c'en est un, sinon le suivant. */
export function nextMonday(d: Date): Date {
  const s = startOfDay(d);
  const dow = (s.getDay() + 6) % 7;
  return dow === 0 ? s : addDays(s, 7 - dow);
}

// ── Vue ──────────────────────────────────────────────────────────────────────

export type PlanViewScale = "year" | "quarter" | "month" | "week";

/** Bornes de la période affichée pour une échelle et une date d'ancrage. */
export function periodBounds(scale: PlanViewScale, anchor: Date): { from: Date; to: Date; label: string } {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (scale === "year") {
    return { from: new Date(y, 0, 1), to: new Date(y, 11, 31), label: String(y) };
  }
  if (scale === "quarter") {
    const q = Math.floor(m / 3);
    return { from: new Date(y, q * 3, 1), to: new Date(y, q * 3 + 3, 0), label: `T${q + 1} ${y}` };
  }
  if (scale === "week") {
    // La vue du lundi : qui a quoi cette semaine (spec/pilot-cible.md, chantier D).
    const from = weekStart(anchor);
    const to = addDays(from, 6);
    return { from, to, label: `S${isoWeekOf(from)} · ${from.getDate()}/${from.getMonth() + 1} → ${to.getDate()}/${to.getMonth() + 1}` };
  }
  return { from: new Date(y, m, 1), to: new Date(y, m + 1, 0), label: `${y}-${String(m + 1).padStart(2, "0")}` };
}

/** Déplace l'ancre d'une période dans le sens demandé. */
export function shiftAnchor(scale: PlanViewScale, anchor: Date, dir: 1 | -1): Date {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (scale === "year") return new Date(y + dir, 0, 1);
  if (scale === "quarter") return new Date(y, m + 3 * dir, 1);
  if (scale === "week") return addDays(weekStart(anchor), 7 * dir);
  return new Date(y, m + dir, 1);
}

/** Position (%) d'une date dans la période, pour placer une barre. */
export function pctInPeriod(d: Date, from: Date, to: Date): number {
  const total = diffDays(from, to) + 1;
  const pos = diffDays(from, d);
  return Math.max(0, Math.min(100, (pos / total) * 100));
}

/** Largeur (%) d'un intervalle [a, b] (bornes incluses) dans la période. */
export function widthInPeriod(a: Date, b: Date, from: Date, to: Date): number {
  const total = diffDays(from, to) + 1;
  const s = Math.max(0, diffDays(from, a));
  const e = Math.min(total, diffDays(from, b) + 1);
  return Math.max(0, ((e - s) / total) * 100);
}

/**
 * Modèles de plan Kestio : des enchaînements de situations éprouvés, à poser en un
 * clic dans « Construis mon plan ». Les clés renvoient aux `intent_contexts` actives ;
 * une clé absente de l'environnement est simplement ignorée.
 */
// Les situations actives varient d'un environnement à l'autre (sous-ensemble V1) :
// chaque modèle liste des équivalents, dans l'ordre de la progression voulue.
export const PLAN_TEMPLATES: { key: string; intentKeys: string[] }[] = [
  { key: "onboarding_90", intentKeys: ["onboard_newcomer", "launch_bizcoach", "level_gap", "keep_level", "certify_team"] },
  { key: "prospection_quarter", intentKeys: ["more_meetings", "conversion_rate", "close_faster", "price_pressure"] },
  { key: "launch_offer", intentKeys: ["launch_offer", "deploy_method", "close_faster", "certify_team"] },
  { key: "relaunch_training", intentKeys: ["launch_bizcoach", "reengage_team", "keep_level", "debrief_real_calls"] },
];

/** Numéro de semaine ISO d'une date. */
export function isoWeekOf(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - y0.getTime()) / 86_400_000 + 1) / 7);
}

// ── Fenêtre glissante ────────────────────────────────────────────────────────
//
// Le plan DÉFILE : l'échelle fixe la largeur de la fenêtre (un an, un
// trimestre, cinq semaines, une semaine), jamais ses bornes. On glisse le
// calendrier, l'en-tête suit. Vers le passé, on s'arrête trois mois avant la
// première action posée ; vers l'avenir, rien n'arrête.

/** Marge de recul autorisée avant la première action du compte. */
export const PLAN_PAST_MARGIN_MONTHS = 3;

/** Largeur de la fenêtre « mois » : cinq semaines pleines, du lundi au dimanche. */
export const PLAN_MONTH_WINDOW_DAYS = 35;

/** `d` + n mois, le jour borné à la longueur du mois d'arrivée (31 janv. + 1 = 28 févr.). */
export function addMonths(d: Date, n: number): Date {
  const y = d.getFullYear(), m = d.getMonth() + n;
  const last = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(d.getDate(), last));
}

/** Premier jour du mois de `d`. */
export function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/**
 * Aligne un début de fenêtre sur la grille de l'échelle : le 1er du mois pour
 * l'année et le trimestre (colonnes = mois, semaines), le lundi pour le mois et
 * la semaine (colonnes = jours). Le défilement reste libre pendant le geste ;
 * l'alignement se fait au relâcher.
 */
export function snapWindowStart(scale: PlanViewScale, d: Date): Date {
  if (scale === "year" || scale === "quarter") return monthStart(d);
  return weekStart(d);
}

/** Bornes de la fenêtre qui commence à `start` (déjà aligné ou non). */
export function windowBounds(scale: PlanViewScale, start: Date): { from: Date; to: Date } {
  const from = startOfDay(start);
  if (scale === "year") return { from, to: addDays(addMonths(from, 12), -1) };
  if (scale === "quarter") return { from, to: addDays(addMonths(from, 3), -1) };
  if (scale === "month") return { from, to: addDays(from, PLAN_MONTH_WINDOW_DAYS - 1) };
  return { from, to: addDays(from, 6) };
}

/** Pas des boutons ‹ › : un cran de la grille, pas une fenêtre entière. */
export function shiftWindowStart(scale: PlanViewScale, start: Date, dir: 1 | -1): Date {
  const s = snapWindowStart(scale, start);
  if (scale === "year") return addMonths(s, 3 * dir);
  if (scale === "quarter") return addMonths(s, dir);
  return addDays(s, 7 * dir);
}

/**
 * Début de fenêtre qui montre « aujourd'hui » avec du recul : un quart de
 * fenêtre de passé derrière, l'avenir devant.
 */
export function windowStartAround(scale: PlanViewScale, today: Date): Date {
  if (scale === "year") return monthStart(addMonths(today, -3));
  if (scale === "quarter") return monthStart(addMonths(today, -1));
  if (scale === "month") return weekStart(addDays(today, -7));
  return weekStart(today);
}

/**
 * Borne basse du défilement : trois mois avant la première action, alignée
 * sur la grille (jamais après la borne brute, quitte à montrer un peu plus).
 * Sans action posée, on part d'aujourd'hui.
 */
export function minWindowStart(scale: PlanViewScale, earliest: Date | null, today: Date): Date {
  return snapWindowStart(scale, monthStart(addMonths(earliest ?? today, -PLAN_PAST_MARGIN_MONTHS)));
}

/** Applique la borne basse ; l'avenir n'est pas borné. */
export function clampWindowStart(start: Date, min: Date | null): Date {
  return min && start < min ? min : start;
}

/**
 * « Préparer » une campagne ne la LANCE que sur demande explicite (`launch: true`, envoyé par un
 * écran après confirmation). Corps absent, vide ou `launch` d'un autre type : préparation seule
 * (audit du lot 5 : le serveur lançait par défaut).
 */
export function prepareLaunchRequested(body: unknown): boolean {
  return !!body && typeof body === "object" && (body as { launch?: unknown }).launch === true;
}

/**
 * « Prolonger d'une semaine » : nouvelle date de fin (AAAA-MM-JJ, jour local), même calcul pour la
 * liste Campagnes et la fiche campagne — annoncée dans la confirmation.
 */
export function extendWeekEnd(endsAt: string | Date): string {
  return toISODate(addDays(new Date(endsAt), 7));
}
